import 'moment-timezone';

import * as moment from 'moment';
import { filter, map, mergeMap } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import {
    deleteAllEntities, deleteEntitiesByPredicate, getActiveEntity, getEntity, resetActiveId,
    resetActiveIds, selectActiveEntities, selectActiveEntity, selectAllEntities, selectEntity,
    setActiveId, setActiveIds, updateEntities, updateEntitiesIds, upsertEntities
} from '@ngneat/elf-entities';
import {
    clearRequestsCache, isRequestCached, selectIsRequestCached, selectRequestCache,
    selectRequestStatus, updateRequestCache, updateRequestStatus
} from '@ngneat/elf-requests';
import { CacheService } from '@shared/services';
import { InventoryTypesRepository } from '@shared/state/inventory/inventory-types.repository';
import { setReservationDates } from '@shared/utils/date';

import { BaseRepository } from '../base.repository';
import { STORETIMEOUT } from '../db';
import { SessionRepository } from '../session/session.repository';
import { Reservation } from './reservation.interface';
import { store } from './reservation.store';

@Injectable({ providedIn: 'root' })
export class ReservationsRepository extends BaseRepository {
	private _filterDate = moment().format('YYYY-MM-DD');
	private excludedStatuses: any = [];

	set filterDate(date) {
		this._filterDate = date;
	}
	get filterDate() {
		return this.filterDate;
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - selects
	 **-------------------------------------------------------------------------------------
	 */
	reservationsStatus$ = store.pipe(selectRequestStatus('reservations'));
	selectReservationCacheStatus = store.pipe(selectRequestCache('reservations'));
	activeReservation$ = store.pipe(selectActiveEntity());
	isCached$ = store.pipe(selectIsRequestCached('reservations'));

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - queries
	 **-------------------------------------------------------------------------------------
	 */
	activeReservation = id => store.query(getEntity(id));
	active = store.query(getActiveEntity());
	queryIsCached = store.query(isRequestCached('reservations'));
	trigger = store.update(updateEntitiesIds(0, 0));
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - constructor
	 **-------------------------------------------------------------------------------------
	 */
	constructor(
		private readonly cache: CacheService,
		private readonly sessionRepo: SessionRepository,
		private readonly inventoryTypesRepo: InventoryTypesRepository
	) {
		super();
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - statuses
	 **-------------------------------------------------------------------------------------
	 */
	set statuses(excluded) {
		this.excludedStatuses = excluded;
	}
	get statuses() {
		return this.excludedStatuses;
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - store
	 **-------------------------------------------------------------------------------------
	 */
	get store() {
		return store;
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - clear
	 **-------------------------------------------------------------------------------------
	 */
	public clear() {
		store.update(deleteAllEntities());
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - clearCache
	 **-------------------------------------------------------------------------------------
	 */
	public clearCache(): void {
		store.update(clearRequestsCache());
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - sleectReservations
	 **-------------------------------------------------------------------------------------
	 */
	public selectReservation$ = id =>
		store.pipe(
			selectEntity(id),
			filter((r: any) => r?.reservation_id),
			mergeMap(async reservation => {
				return {
					...reservation,
					...(await this.setReservationDates({ reservation })),
					status: reservation.reservation_status.replace(/ /gi, '').toLowerCase(),
				};
			})
		);
	/*
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 ** METHOD NAME - getReservation
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 */
	public getReservation = id => store.query(getEntity(id));
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - setActiveReservation
	 **-------------------------------------------------------------------------------------
	 */
	public setActiveReservation = id => store.update(setActiveId(id));
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - setActiveReservation
	 **-------------------------------------------------------------------------------------
	 */
	public clearActiveReservation = store.update(resetActiveId());
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - select all reservations
	 **-------------------------------------------------------------------------------------
	 */
	reservations$ = store.pipe(selectAllEntities());
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - mutations
	 **-------------------------------------------------------------------------------------
	 */
	clearStore = () => {
		this.clearCache();
		store.update(resetActiveIds());
		store.update(deleteEntitiesByPredicate(e => true));
		store.update(deleteAllEntities());
	};

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - select all reservations
	 **-------------------------------------------------------------------------------------
	 */
	reservationsSchedule$ = store.pipe(
		selectActiveEntities(),
		map(resp => {
			return this.groupBy(
				resp
					.map(i => ({
						...i,
						time: moment(i.start_date).format('hh:mm a'),
						status: i.reservation_status.replace(/ /gi, '').toLowerCase(),
						start_time: moment(i.start_date).format('MM/DD/YYYY h:mm a'),
						end_time: moment(i.end_date).format('MM/DD/YYYY h:mm a'),
						i_status: i?.invoice_status?.replace(/ /gi, '').toLowerCase(),
					}))
					.filter((r: any) => {
						const compareDate = moment(this._filterDate);
						const startDate = moment(r.start_date).startOf('day');
						const endDate = moment(r.end_date).endOf('day');
						const isBetween = compareDate.isBetween(startDate, endDate, 'days', '[]');
						return isBetween;
					})
					.filter(i => !this.excludedStatuses.includes(i.status))
					.filter(i => !this.inventoryTypesRepo.excludedInventoryTypes.includes(i.inventory_type_id)),
				'time',
				'reservations'
			);
		})
	);

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - update
	 **-------------------------------------------------------------------------------------
	 */
	public update(id: Reservation['reservation_id'], data) {
		store.update(
			updateEntities(id, entity => ({
				...entity,
				...data,
			}))
		);
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - addReservations
	 **-------------------------------------------------------------------------------------
	 */
	public addReservations(reservations) {
		store.update(
			setActiveIds(reservations.map(i => i.reservation_id)),
			upsertEntities(
				reservations.map(i => ({
					...i,
					name: i.counted > 1 ? 'Multiple Products' : i.name,
				}))
			),
			updateRequestStatus('reservations', 'success')
			//updateRequestCache(`reservations`, { value: 'full' })
		);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - addCustomers
	 **-------------------------------------------------------------------------------------
	 */
	public addReservation(reservation) {
		store.update(
			updateRequestCache(`reservation-${reservation.reservation_id}`, {
				value: 'partial',
				ttl: STORETIMEOUT,
			}),
			upsertEntities([reservation]),
			updateRequestStatus(`reservation-${reservation.reservation_id}`, 'success')
		);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - setReservationDates
	 **-------------------------------------------------------------------------------------
	 */
	public async setReservationDates({ reservation }) {
		if (!reservation) return {};
		const reservationTimezone = this.sessionRepo.get(reservation.company_id, 'timezone');
		return setReservationDates({
			reservationTimezone,
			startDate: reservation.start_date,
			endDate: reservation.end_date,
			createTime: reservation?.reservation_create_time,
		});
	}
}
